"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { toast } from "sonner";

type Summary = {
  pending: number;
  running: number;
  done: number;
  failed: number;
  skipped: number;
  total: number;
  byKind?: {
    download_pending?: number;
    list_pending?: number;
    asr_pending?: number;
  };
};

type Job = {
  id: string;
  kind: string;
  target: string;
  desc?: string;
  author?: string;
  status: string;
  message?: string;
};

type AsrProgress = {
  status?: string;
  total?: number;
  done?: number;
  failed?: number;
  skipped?: number;
  percent?: number;
  finished?: number;
  model?: string;
  current?: {
    index?: number;
    total?: number;
    path?: string;
    author?: string;
    aweme_id?: string;
    score_before?: number;
    note?: string;
  } | null;
  rebuild?: Record<string, number>;
  items?: Array<Record<string, unknown>>;
  updated_at?: string;
};

export default function IngestPage() {
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<"video_only" | "list_only">("video_only");
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [auto, setAuto] = useState(false);
  const [preferAsr, setPreferAsr] = useState(false);
  const [currentMsg, setCurrentMsg] = useState("");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [asr, setAsr] = useState<AsrProgress | null>(null);
  const [lastLog, setLastLog] = useState("");
  const busy = useRef(false);

  const refresh = useCallback(async () => {
    const [jRes, aRes] = await Promise.all([
      fetch("/api/jobs", { cache: "no-store" }),
      fetch("/api/asr/progress", { cache: "no-store" }),
    ]);
    const jData = await jRes.json();
    const aData = await aRes.json();
    setSummary(jData.summary);
    setJobs((jData.items as Job[]).slice().reverse().slice(0, 80));
    setAsr(aData);
  }, []);

  useEffect(() => {
    queueMicrotask(() => void refresh());
    const t = window.setInterval(() => void refresh(), 1500);
    return () => window.clearInterval(t);
  }, [refresh]);

  const queueProgress = useMemo(() => {
    if (!summary || summary.total === 0) return 0;
    const finished = summary.done + summary.failed + summary.skipped;
    return Math.round((finished / summary.total) * 100);
  }, [summary]);

  const asrPercent = asr?.percent ?? 0;
  const asrRunning = asr?.status === "running";

  const processOne = useCallback(async () => {
    if (busy.current) return true;
    busy.current = true;
    setProcessing(true);
    setCurrentMsg("处理中…");
    try {
      const qs = preferAsr ? "?prefer=asr" : "";
      const res = await fetch(`/api/jobs/process-next${qs}`, { method: "POST" });
      const data = await res.json();
      setLastLog(JSON.stringify(data, null, 2));
      await refresh();
      if (data.idle) {
        setCurrentMsg("队列空闲");
        setAuto(false);
        toast.message("通用队列已清空");
        return true;
      }
      const st = data.job?.status;
      const msg = data.job?.message || st;
      setCurrentMsg(
        `${data.job?.kind || "?"} · ${st} · ${(data.job?.author || "").slice(0, 12)} · ${String(msg).slice(0, 48)}`,
      );
      if (st === "done") toast.success(String(msg || "完成 1 条").slice(0, 80));
      else if (st === "skipped") toast.message(String(msg || "跳过"));
      else if (st === "failed") toast.error(String(msg || "失败").slice(0, 80));
      return false;
    } catch (e) {
      toast.error(String(e));
      setAuto(false);
      setCurrentMsg(String(e));
      return true;
    } finally {
      setProcessing(false);
      busy.current = false;
    }
  }, [preferAsr, refresh]);

  useEffect(() => {
    if (!auto) return;
    let cancelled = false;
    const loop = async () => {
      while (!cancelled && auto) {
        const idle = await processOne();
        if (idle || cancelled) break;
        await new Promise((r) => setTimeout(r, 600));
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [auto, processOne]);

  async function enqueue(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await fetch("/api/ingest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: url.trim(),
          mode: mode === "list_only" ? "list_only" : "video_only",
        }),
      });
      const data = await res.json();
      setLastLog(JSON.stringify(data, null, 2));
      if (!res.ok) toast.error(String(data.error || "失败"));
      else if (data.status === "skipped_exists") toast.message("已存在，跳过");
      else toast.success(String(data.message || "已入队"));
      await refresh();
    } catch (err) {
      toast.error(String(err));
    } finally {
      setLoading(false);
    }
  }

  async function enrichStats() {
    setLoading(true);
    try {
      const res = await fetch("/api/enrich", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 10, sleep: 4 }),
      });
      const data = await res.json();
      setLastLog(JSON.stringify(data, null, 2));
      if (!res.ok || data.ok === false) toast.error(String(data.error || data.message || "补数失败"));
      else toast.success(`互动补数完成 ${JSON.stringify(data.summary)}`);
      await refresh();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function startAsrBatch() {
    setLoading(true);
    try {
      const res = await fetch("/api/asr/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 20, model: "medium", minScore: 55 }),
      });
      const data = await res.json();
      setLastLog(JSON.stringify(data, null, 2));
      if (!res.ok) toast.error(String(data.error || "启动失败"));
      else toast.success(String(data.message || "ASR 已启动"));
      await refresh();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function enqueueAsrJobs() {
    setLoading(true);
    try {
      const res = await fetch("/api/jobs/transcribe-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 20, qualityMin: "good" }),
      });
      const data = await res.json();
      setLastLog(JSON.stringify(data, null, 2));
      if (!res.ok) toast.error(String(data.error || "失败"));
      else {
        toast.success(String(data.message || `入队 ${data.enqueued}`));
        setPreferAsr(true);
      }
      await refresh();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  const statusColor = (s: string) =>
    s === "done"
      ? "text-emerald-400"
      : s === "failed"
        ? "text-red-400"
        : s === "skipped"
          ? "text-zinc-500"
          : s === "running"
            ? "text-sky-400"
            : "text-amber-300";

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">提交与队列</h1>
        <p className="mt-1 text-sm text-zinc-500">
          下载/列表逐条处理；ASR 用本机{" "}
          <strong className="text-zinc-300">faster-whisper medium</strong>
          ，无需云端密钥；转写后自动重分型+打分。
        </p>
      </header>

      {/* Queue progress */}
      <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span className="font-medium text-zinc-300">通用队列进度</span>
          <span className="tabular-nums text-emerald-300">{queueProgress}%</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-zinc-800">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${queueProgress}%` }}
          />
        </div>
        {summary && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {(
              [
                ["待处理", summary.pending, "text-amber-300"],
                ["运行中", summary.running, "text-sky-400"],
                ["完成", summary.done, "text-emerald-400"],
                ["跳过", summary.skipped, "text-zinc-500"],
                ["失败", summary.failed, "text-red-400"],
                ["总计", summary.total, "text-zinc-300"],
              ] as const
            ).map(([k, v, c]) => (
              <div
                key={k}
                className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-2 py-2 text-center"
              >
                <div className="text-[10px] text-zinc-500">{k}</div>
                <div className={`text-lg font-semibold tabular-nums ${c}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        {summary?.byKind && (
          <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500">
            <span>下载待处理 {summary.byKind.download_pending ?? 0}</span>
            <span>·</span>
            <span>列表待处理 {summary.byKind.list_pending ?? 0}</span>
            <span>·</span>
            <span className="text-violet-300">
              ASR 待处理 {summary.byKind.asr_pending ?? 0}
            </span>
          </div>
        )}
        <p className="truncate text-[11px] text-zinc-500">
          当前：{currentMsg || (auto ? "自动连跑中…" : processing ? "处理中…" : "空闲")}
        </p>
      </div>

      {/* ASR dedicated progress */}
      <div className="space-y-3 rounded-xl border border-violet-500/25 bg-violet-500/5 p-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-violet-200">
            ASR 批量进度
            {asr?.model ? ` · ${asr.model}` : " · small"}
            {asrRunning ? " · 运行中" : " · 空闲"}
          </span>
          <span className="tabular-nums text-violet-300">{asrPercent}%</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-zinc-800">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              asrRunning ? "bg-violet-400 animate-pulse" : "bg-violet-500"
            }`}
            style={{ width: `${asrPercent}%` }}
          />
        </div>
        <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
          <div>
            <div className="text-zinc-500">完成</div>
            <div className="text-base font-semibold text-emerald-300">
              {asr?.done ?? 0}
            </div>
          </div>
          <div>
            <div className="text-zinc-500">失败</div>
            <div className="text-base font-semibold text-red-400">
              {asr?.failed ?? 0}
            </div>
          </div>
          <div>
            <div className="text-zinc-500">跳过</div>
            <div className="text-base font-semibold text-zinc-400">
              {asr?.skipped ?? 0}
            </div>
          </div>
          <div>
            <div className="text-zinc-500">总数</div>
            <div className="text-base font-semibold text-zinc-200">
              {asr?.total ?? 0}
            </div>
          </div>
        </div>
        {asr?.current && (
          <p className="truncate text-[11px] text-violet-200/80">
            正在：[{asr.current.index}/{asr.current.total}]{" "}
            {asr.current.author} · {asr.current.aweme_id || asr.current.path || asr.current.note}
          </p>
        )}
        {asr?.rebuild && (
          <p className="text-[11px] text-zinc-500">
            最近重建：教学 {asr.rebuild.teaching_items ?? "—"} · 已转写{" "}
            {asr.rebuild.with_transcript ?? "—"} · 均分 {asr.rebuild.avg_score ?? "—"}
          </p>
        )}
      </div>

      <form
        onSubmit={enqueue}
        className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"
      >
        <textarea
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          rows={3}
          placeholder={
            "单视频：https://www.douyin.com/video/...\n账号：https://www.douyin.com/user/MS4wLjABAAAA..."
          }
          className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm outline-none focus:border-emerald-500/50"
        />
        <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "video_only"}
              onChange={() => setMode("video_only")}
            />
            只下这一条
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "list_only"}
              onChange={() => setMode("list_only")}
            />
            拉作者列表 → 再逐条下
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-zinc-100 py-2 text-sm font-medium text-zinc-900 hover:bg-white disabled:opacity-50"
        >
          {loading ? "入队中…" : "加入下载/列表队列"}
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void processOne()}
          disabled={processing || auto || asrRunning}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {processing ? "处理中…" : "处理下一条"}
        </button>
        <button
          type="button"
          onClick={() => setAuto((v) => !v)}
          disabled={asrRunning}
          className={
            "rounded-lg border px-4 py-2 text-sm " +
            (auto
              ? "border-amber-500/50 bg-amber-500/10 text-amber-200"
              : "border-zinc-700 text-zinc-300")
          }
        >
          {auto ? "■ 停止自动" : "▶ 自动连跑"}
        </button>
        <label className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2 text-xs text-zinc-400">
          <input
            type="checkbox"
            checked={preferAsr}
            onChange={(e) => setPreferAsr(e.target.checked)}
          />
          优先跑 ASR 任务
        </label>
        <button
          type="button"
          onClick={() => void startAsrBatch()}
          disabled={loading || asrRunning}
          className="rounded-lg border border-violet-500/40 bg-violet-500/15 px-4 py-2 text-sm text-violet-100 hover:bg-violet-500/25 disabled:opacity-50"
        >
          {asrRunning ? "ASR 运行中…" : "启动 ASR 批量（medium×15）"}
        </button>
        <button
          type="button"
          onClick={() => void enrichStats()}
          disabled={loading}
          className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
        >
          补互动数（慢·防风控）
        </button>
        <button
          type="button"
          onClick={() => void enqueueAsrJobs()}
          disabled={loading}
          className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300"
        >
          入队 ASR 到通用队列
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-400"
        >
          刷新
        </button>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-zinc-300">任务列表</h2>
        <ul className="max-h-80 space-y-1 overflow-auto text-xs">
          {jobs.map((j) => (
            <li
              key={j.id}
              className="rounded border border-zinc-800 bg-zinc-900/40 px-2 py-1.5"
            >
              <div className="flex justify-between gap-2">
                <span className="text-zinc-400">
                  <span
                    className={
                      j.kind === "transcribe" ? "text-violet-300" : "text-zinc-500"
                    }
                  >
                    [{j.kind}]
                  </span>{" "}
                  {(j.author || "").slice(0, 10)}{" "}
                  {j.target.length > 28 ? j.target.slice(0, 28) + "…" : j.target}
                </span>
                <span className={statusColor(j.status)}>{j.status}</span>
              </div>
              {(j.desc || j.message) && (
                <p className="mt-0.5 line-clamp-1 text-zinc-600">
                  {j.message || j.desc}
                </p>
              )}
              {j.kind === "download_video" && j.status === "done" && (
                <Link
                  href={"/library/" + j.target}
                  className="text-emerald-500 hover:underline"
                >
                  打开素材 →
                </Link>
              )}
            </li>
          ))}
          {jobs.length === 0 && <li className="text-zinc-600">暂无任务</li>}
        </ul>
      </section>

      <div className="rounded-xl border border-zinc-800 p-4 text-xs leading-relaxed text-zinc-500">
        <p className="font-medium text-zinc-300">ASR 说明（更准 + 重分型）</p>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          <li>
            <strong className="text-zinc-300">不需要云端密钥</strong>：本机
            faster-whisper <code className="text-zinc-400">small</code>
          </li>
          <li>
            「启动 ASR 批量」= 后台独立进程，自带进度文件{" "}
            <code className="text-zinc-400">catalog/asr_progress.json</code>
          </li>
          <li>
            每条转写后会用<strong className="text-zinc-300">口播+文案</strong>
            重做 content_type / score / topics，并写 lesson.md
          </li>
          <li>搞笑/解说/新闻在口播层会被再次剔除出教学主表</li>
        </ul>
      </div>

      {lastLog && (
        <pre className="max-h-36 overflow-auto rounded-lg border border-zinc-800 p-3 text-[10px] text-zinc-600">
          {lastLog}
        </pre>
      )}
    </div>
  );
}
